import { X, Check } from "lucide-react";

const before = [
  "Hunt through crowded menus",
  "Tap through multiple pages",
  "Parse overwhelming layouts",
  "Click tiny, imprecise targets",
  "Start over when you get lost",
];

const after = [
  "One sentence. One intent.",
  "Skip straight to the result",
  "Only what matters, surfaced",
  "No precision required",
  "Always reversible, always clear",
];

const BeforeAfter = () => {
  return (
    <section
      id="before-after"
      data-testid="before-after-section"
      className="relative py-28 md:py-36 border-t border-white/[0.06]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1">
            <span className="h-1.5 w-1.5 rounded-full bg-amber-400 shadow-[0_0_10px_rgba(251,191,36,0.9)]" />
            <span className="font-mono uppercase tracking-[0.2em] text-[10.5px] text-zinc-400">
              Before · after
            </span>
          </div>
          <h2 className="mt-6 font-display font-semibold tracking-tight text-white text-3xl md:text-4xl lg:text-5xl text-balance">
            The same task. A completely different feeling.
          </h2>
          <p className="mt-6 font-body text-zinc-400 text-base md:text-lg leading-relaxed max-w-2xl">
            Equal doesn't just add a feature — it replaces the effort of using the web with the clarity of
            simply asking.
          </p>
        </div>

        <div className="mt-14 grid md:grid-cols-2 gap-5">
          {/* Before */}
          <div
            data-testid="before-card"
            className="relative rounded-2xl border border-white/[0.08] bg-white/[0.015] p-7 md:p-8"
          >
            <div className="flex items-center justify-between">
              <div className="font-mono uppercase tracking-[0.2em] text-[10.5px] text-zinc-500">
                Before Equal
              </div>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-rose-300/80">
                friction
              </span>
            </div>
            <h3 className="mt-4 font-display text-white text-2xl tracking-tight">
              Using the web today
            </h3>
            <ul className="mt-6 space-y-3">
              {before.map((b, i) => (
                <li key={i} className="flex items-start gap-3 font-body text-[15px] text-zinc-400">
                  <span className="mt-0.5 h-5 w-5 rounded-full bg-white/[0.03] border border-white/[0.06] flex items-center justify-center flex-shrink-0">
                    <X className="h-3 w-3 text-zinc-500" strokeWidth={2} />
                  </span>
                  {b}
                </li>
              ))}
            </ul>
          </div>

          {/* After */}
          <div
            data-testid="after-card"
            className="relative rounded-2xl border border-indigo-500/30 bg-gradient-to-b from-indigo-500/[0.06] to-transparent p-7 md:p-8 shadow-[0_20px_60px_-30px_rgba(99,102,241,0.5)]"
          >
            <div className="absolute -inset-px rounded-2xl bg-gradient-to-br from-indigo-500/20 via-transparent to-violet-500/20 -z-10 blur-2xl" />
            <div className="flex items-center justify-between">
              <div className="font-mono uppercase tracking-[0.2em] text-[10.5px] text-indigo-300">
                With Equal
              </div>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-indigo-300">
                flow
              </span>
            </div>
            <h3 className="mt-4 font-display text-white text-2xl tracking-tight">
              The web, on your terms
            </h3>
            <ul className="mt-6 space-y-3">
              {after.map((a, i) => (
                <li key={i} className="flex items-start gap-3 font-body text-[15px] text-white">
                  <span className="mt-0.5 h-5 w-5 rounded-full bg-indigo-500/20 border border-indigo-500/40 flex items-center justify-center flex-shrink-0 shadow-[0_0_12px_rgba(99,102,241,0.4)]">
                    <Check className="h-3 w-3 text-indigo-200" strokeWidth={2.5} />
                  </span>
                  {a}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BeforeAfter;
